import { useEffect, useState } from 'react'

import { TOAST_CLASSNAME } from 'src/constants'
import useEvents from 'src/hooks/useEvents'

import { Drawer as AntDrawer, DrawerProps } from 'antd'

export interface WithOutsideClick {
    enableOutsideClickClose?: boolean
    ignoreOutsideClickElementIds?: string[]
}

interface MaskEnabled extends DrawerProps {
    mask?: true
}

interface MaskDisabled extends DrawerProps, WithOutsideClick {
    mask?: false
}

export type Props = MaskEnabled | MaskDisabled

export const DRAWER_MASK_TEST_ID = 'drawer-mask'
export const TEST_DRAWER_CONTAINER_ID = 'test-drawer-container'

const Drawer = (props: Props) => {
    const { mask, afterOpenChange, onClose } = props

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [enableEvents, setEnableEvents] = useState(false)

    useEffect(() => {
        if (mask === false) {
            const { enableOutsideClickClose = false } = props
            setEnableEvents(enableOutsideClickClose && drawerOpen)
        }
    }, [mask, drawerOpen, (props as MaskDisabled).enableOutsideClickClose])

    // We don't want to listen to click on other drawers/preview overlays
    const eventContainer = document.getElementById('root')
    useEvents(
        eventContainer,
        {
            // If default mask is disabled, we mimic the closing of the drawer when needed
            // using code can decide whether to enable it or not, and which elements to ignore
            click: (event) => {
                if (drawerOpen && event.target instanceof HTMLElement) {
                    const { ignoreOutsideClickElementIds = [] } = props as MaskDisabled

                    // Clicking a toast should not close the drawer
                    const toastClicked = checkParentClassnamesRecursive(event.target, [TOAST_CLASSNAME])
                    const elementToIgnoreClicked = checkParentIdsRecursive(event.target, ignoreOutsideClickElementIds)

                    if (!toastClicked && !elementToIgnoreClicked) {
                        onClose(null)
                    }
                }
            },
        },
        enableEvents,
    )

    return (
        <AntDrawer
            {...props}
            afterOpenChange={(open) => {
                afterOpenChange?.(open)
                setDrawerOpen(open)
            }}
            onClose={(e) => {
                setDrawerOpen(false)
                onClose?.(e)
            }}
            // Prevent flaky storyshots by forcing the drawer to render
            forceRender={props.forceRender || import.meta.env.STORYBOOK_RUNNING === 'true'}
            // Storyshots can't attach the drawer to the body, so we attach it to a div with a known id
            getContainer={() =>
                import.meta.env.STORYBOOK_RUNNING === 'true' ? document.getElementById(TEST_DRAWER_CONTAINER_ID) : null
            }
            // Couldn't find another way of targeting the mask element, if you don't like it feel free to find a better solution :)
            maskClassName={import.meta.env.VITEST ? DRAWER_MASK_TEST_ID : undefined}
        >
            {props.children}
        </AntDrawer>
    )
}

const checkParentIdsRecursive = (element: HTMLElement, ids?: string[]): boolean => {
    if (!element) {
        return false
    }

    if (ids?.includes(element.id)) {
        return true
    }

    if (element.parentElement === null) {
        return false
    }

    return checkParentIdsRecursive(element.parentElement, ids)
}

const checkParentClassnamesRecursive = (element: HTMLElement, classnames?: string[]): boolean => {
    if (!element) {
        return false
    }

    if (classnames?.some((classname) => element.classList.contains(classname))) {
        return true
    }

    if (element.parentElement === null) {
        return false
    }

    return checkParentClassnamesRecursive(element.parentElement, classnames)
}

export default Drawer
